import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookingApi, unwrap } from '@/lib/api/client';
import type {
  ArchiveResult,
  PaymentReviewList,
  ReconcileResult,
  SweepResult,
} from './types';

const QUERY_KEY = ['payment-review'];

/**
 * The cutoff between "chase this" and "too old to chase": midnight on 1 January
 * of the current year. Everything before it can be closed out unreviewed;
 * everything after it is what the reconciliation searches PayPal for.
 */
export function yearStart() {
  return new Date(new Date().getFullYear(), 0, 1);
}

export const yearStartIso = () => `${yearStart().getFullYear()}-01-01T00:00:00`;

/** The standing list. Stored on the API, so it survives between sessions. */
export function usePaymentReview() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      unwrap<PaymentReviewList>(
        bookingApi.post('v2/admin/bookings/payment-review', null)
      ),
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    // The bookings grid counts the same money, so it goes stale with this.
    void queryClient.invalidateQueries({ queryKey: ['manage-bookings'] });
  };
}

/** Asks PayPal for payments matching the bookings currently on the list. */
export function useSweep() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: () =>
      unwrap<SweepResult>(
        bookingApi.post('v2/admin/bookings/payment-review/sweep', null)
      ),
    onSuccess: invalidate,
  });
}

/**
 * `apply: false` is a preview — the API stages exactly what it would do and
 * then throws the changes away. Nobody should be asked to authorise a mass
 * write to historical payments sight unseen.
 */
export function useArchiveBacklog() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (apply: boolean) =>
      unwrap<ArchiveResult>(
        bookingApi.post(
          `v2/admin/bookings/payment-review/archive?before=${encodeURIComponent(yearStartIso())}&apply=${apply}`,
          null
        )
      ),
    onSuccess: (_data, apply) => {
      if (apply) invalidate();
    },
  });
}

/** Same preview-then-apply contract as the archive. */
export function useReconcile() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (apply: boolean) =>
      unwrap<ReconcileResult>(
        bookingApi.post(
          `v2/admin/bookings/payment-review/reconcile?from=${encodeURIComponent(yearStartIso())}&apply=${apply}`,
          null
        )
      ),
    onSuccess: (_data, apply) => {
      if (apply) invalidate();
    },
  });
}

// ------------------------------------------------------ per-row decisions

/** Records the PayPal payment against the booking. */
export function useAttachPayment() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      bookingId,
      transactionId,
    }: {
      bookingId: string;
      transactionId: string;
    }) =>
      unwrap<unknown>(
        bookingApi.post(
          `v2/admin/bookings/${bookingId}/attach-payment?transactionId=${encodeURIComponent(transactionId)}`,
          null
        )
      ),
    onSuccess: invalidate,
  });
}

/** Rules a suggestion out. Remembered, so it is not offered again next sweep. */
export function useRejectPayment() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      bookingId,
      transactionId,
    }: {
      bookingId: string;
      transactionId: string;
    }) =>
      unwrap<unknown>(
        bookingApi.post(
          `v2/admin/bookings/${bookingId}/reject-payment?transactionId=${encodeURIComponent(transactionId)}`,
          null
        )
      ),
    onSuccess: invalidate,
  });
}

/** Records that the booking was given away on purpose and no payment is missing. */
export function useCompBooking() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: string; reason: string }) =>
      unwrap<unknown>(
        bookingApi.post(
          `v2/admin/bookings/${bookingId}/comp?reason=${encodeURIComponent(reason)}`,
          null
        )
      ),
    onSuccess: invalidate,
  });
}

/** Cancels the booking and releases the spot. */
export function useCancelUnpaid() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (bookingId: string) =>
      unwrap<unknown>(
        bookingApi.post(`v2/admin/bookings/${bookingId}/cancel-unpaid`, null)
      ),
    onSuccess: invalidate,
  });
}
